import { isEmpty, type TeamData, type TeamId } from '@terros/common'
import { type GetHookResponse } from '../hooks'
import { type UseTeamProps, useTeams } from './list'

type Props = UseTeamProps & {
  filter?: (team: TeamData) => boolean
}

export function useTeamOutline({ filter, ...input }: Props = {}): GetHookResponse<TeamWithChildren[]> {
  const teams = useTeams(input)
  const orgTeams = teams.data
  if (isEmpty(orgTeams)) {
    return {
      ...teams,
      data: [],
    }
  }
  return {
    ...teams,
    data: filter ? buildTeamOutline(orgTeams.filter(filter)) : buildTeamOutline(orgTeams),
  }
}

export type TeamWithChildren = TeamData & {
  children: TeamWithChildren[]
}

export function buildTeamOutline(teams: TeamData[]): TeamWithChildren[] {
  const teamsWithChildren: TeamWithChildren[] = teams.map((t) => ({ ...t, children: [] }))

  const map = new Map(teamsWithChildren.map((t) => [t.teamId, t]))

  const roots: TeamWithChildren[] = []

  teamsWithChildren.forEach((t) => {
    const parent = findOutlineParent(t, map)
    if (!parent) {
      roots.push(t)
      return
    }
    parent.children.push(t)
  })

  return roots
}

export function toTeamOutlineMap(outline: TeamWithChildren[]): Map<TeamId, TeamWithChildren> {
  const map = new Map<TeamId, TeamWithChildren>()
  const visit = (team: TeamWithChildren): void => {
    map.set(team.teamId, team)
    team.children.forEach(visit)
  }
  outline.forEach(visit)
  return map
}

function findOutlineParent(team: TeamWithChildren, map: Map<TeamId, TeamWithChildren>): TeamWithChildren | undefined {
  if (!team.parentId) return
  const parent = map.get(team.parentId)
  if (!parent) return
  const cycleTeamIds = getCycleTeamIds(team, map)
  if (cycleTeamIds && team.teamId === minTeamId(cycleTeamIds)) return
  return parent
}

function getCycleTeamIds(team: TeamWithChildren, map: Map<TeamId, TeamWithChildren>): TeamId[] | undefined {
  const path: TeamId[] = []
  const visited = new Set<TeamId>()
  let current: TeamWithChildren | undefined = team

  while (current && !visited.has(current.teamId)) {
    visited.add(current.teamId)
    path.push(current.teamId)
    current = current.parentId ? map.get(current.parentId) : undefined
    if (current?.teamId === team.teamId) return path
  }

  return undefined
}

function minTeamId(teamIds: TeamId[]): TeamId {
  return teamIds.reduce((lowest, teamId) => (teamId < lowest ? teamId : lowest))
}
